import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import SEO from "@/components/common/SEO";
import { homeMeta, siteUrl } from "@/lib/seoMeta";
import HomeHero from "@/components/home/HomeHero";
import StatsBar from "@/components/home/StatsBar";
import ProductShowcase from "@/components/home/ProductShowcase";
import CategoryExplorer from "@/components/home/CategoryExplorer";
import IndustriesSection from "@/components/home/IndustriesSection";
import GlobalPresenceSection from "@/components/global-presence/GlobalPresenceSection";
import WhyShingare from "@/components/home/WhyShingare";
import FacilitySection from "@/components/home/FacilitySection";
import ClientLogos from "@/components/home/ClientLogos";
import Testimonials from "@/components/home/Testimonials";
import QuoteCTA from "@/components/home/QuoteCTA";
import useHomeCatalogue from "@/components/home/useHomeCatalogue";

function HomeSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading homepage">
      <div className="h-[640px] animate-pulse bg-[#071B3A]" />
      <div className="mx-auto -mt-20 max-w-[1320px] px-5 sm:px-8 lg:px-10"><div className="h-28 animate-pulse rounded-2xl bg-white shadow-lg" /></div>
    </div>
  );
}

/**
 * Homepage. Content is CMS-driven (`/home-page/public`, edited in Admin → Home Page);
 * product/category sections use live catalogue data. Section order = the visual
 * story: hero → trust bar → products → category explorer → industries (navy) →
 * global presence map (navy, continues the industries story) → why Shingare → facility (navy, immersive) → clients → verified testimonials
 * (only if real) → quote CTA (navy). Each section is its own component.
 */
export default function HomePage() {
  const { settings } = useSettings();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // `alive` guard (same pattern as useHomeCatalogue/useContactStats): under StrictMode's
    // dev-only double-invoke this effect fires twice in a row, so without it two in-flight
    // requests could each try to set state on an already-unmounted first pass. Harmless in
    // today's production build (StrictMode's double-invoke doesn't happen there), but this
    // keeps the effect correct instead of relying on that.
    let alive = true;
    api.get("/home-page/public")
      .then((res) => { if (alive) setPage(res.data); })
      .catch(() => { if (alive) setError("Unable to load homepage content."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const catalogue = useHomeCatalogue();

  const seo = page?.seo || {};
  const jsonLd = useMemo(() => {
    const origin = siteUrl(process.env.REACT_APP_SITE_URL);
    const sameAs = ["social_facebook", "social_linkedin", "social_youtube", "social_twitter", "social_instagram"].map((k) => settings?.[k]).filter((u) => u && u.trim());
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: settings?.company_name || "Shingare Industries",
      url: origin,
      logo: `${origin}/sipl-logo.png`,
      ...(settings?.phone ? { telephone: settings.phone } : {}),
      ...(settings?.email ? { email: settings.email } : {}),
      ...(settings?.address ? { address: { "@type": "PostalAddress", streetAddress: String(settings.address).replace(/,?\s*\n\s*/g, ", ") } } : {}),
      ...(sameAs.length ? { sameAs } : {}),
    };
  }, [settings]);

  if (loading) return <HomeSkeleton />;
  if (error) return <div className="mx-auto max-w-7xl px-6 py-24 text-center text-slate-600 sm:px-8 lg:px-12">{error}</div>;

  return (
    <div className="bg-white">
      <SEO {...homeMeta({ cms: seo, settings })} jsonLd={jsonLd} />
      <HomeHero data={page} />
      <StatsBar data={page} />
      <ProductShowcase data={page} />
      <CategoryExplorer categories={catalogue.categories} data={page} />
      <IndustriesSection data={page} />
      <GlobalPresenceSection data={page} />
      <WhyShingare data={page} />
      <FacilitySection data={page} />
      <ClientLogos data={page} />
      <Testimonials />
      <QuoteCTA data={page} />
    </div>
  );
}
